import { AddCircleOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { type FC, useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type DropdownItem, SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import type { Job } from "../../../../utils/models";
import CreateJobDialog from "./CreateJobDialog";
import { GROUP_PREFIX, type JobGroup } from "./jobGroups";

/**
 * The one place a department's enabled job list is edited, opened from two entry points: the Jobs
 * section on the Jobs tab ("+ Manage jobs") and the Integrations tab's Synced Jobs aside ("+ Update").
 *
 * The selection is a DRAFT: nothing reaches `formData.jobIds` until Confirm, and the whole tab is still
 * one buffered form behind a single Save — so this dialog never writes to the server. The exception is
 * job CREATION, which really does hit the catalog endpoint immediately; a job created here is added to
 * the draft so the operator doesn't have to find and tick it afterwards.
 */
export const ManageJobsDialog: FC<{
	open: boolean;
	onClose: () => void;
	/** The full catalog, already bucketed into department groups. */
	groups: JobGroup[];
	/** Currently enabled job ids — the draft's starting point, re-read on every open. */
	selectedJobIds: string[];
	onConfirm: (jobIds: string[]) => void;
	/**
	 * Enables the "Create new job" entry point. Omitted for an unsaved department, which has no id to
	 * attach a new job to.
	 */
	departmentId?: string;
	/** Rejected as duplicates by the create form. */
	existingJobNames: string[];
	/** Fired after a job is created, so the caller can refresh the department's own copy of the catalog. */
	onJobCreated: (job: Job) => void;
	/** Names the surface that opened the dialog in analytics (`shifts` / `integrations`). */
	source: string;
	dataTestId?: string;
}> = ({ open, onClose, groups, selectedJobIds, onConfirm, departmentId, existingJobNames, onJobCreated, source, dataTestId = "manage-jobs-dialog" }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const selectorId = useId();
	const [draftJobIds, setDraftJobIds] = useState<string[]>(selectedJobIds);
	const [createJobOpen, setCreateJobOpen] = useState(false);

	// Re-seed the draft from the live selection on each OPEN: the dialog stays mounted between
	// openings, so without this a cancelled edit would still be sitting in the list next time.
	//
	// `open` is deliberately the only dependency. `selectedJobIds` is the parent's `formData.jobIds`,
	// a fresh array identity on every `setFormData` — and creating a job from inside this dialog calls
	// `onJobCreated` → parent `setFormData`. With `selectedJobIds` in the deps that re-fired the effect
	// *while the dialog was still open* and reset the draft, silently discarding every un-tick made
	// before the create. A newly created job still reaches the draft: `handleJobCreated` adds it.
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-seeding must happen on open only — see above.
	useEffect(() => {
		if (open) setDraftJobIds(selectedJobIds);
	}, [open]);

	/**
	 * Grouped picker items: a header row per department group (clicking it toggles all its jobs via
	 * `SystemDropdown`'s `groupValues`), followed by that group's jobs beneath it. Folding a section is
	 * the dropdown's own `collapsibleGroups` — hand-rolling it by dropping the child items made the
	 * whole section disappear, since the panel drops a header left with no visible children (APP-725).
	 */
	/**
	 * Job ids the department has enabled that the picker cannot show — the `unknown` sentinel, or any
	 * job missing from the catalog this screen loaded. `SystemDropdown` rebuilds its selection from its
	 * own items and drops everything else (`useDropdownState`'s `idsToValues`), so without this an
	 * ADDITIVE edit silently removes them: the save then carries a deletion the user never asked for
	 * and the backend refuses it with DAT-464's "still in use" 409, naming a job they cannot even see.
	 */
	const unrepresentedJobIds = useMemo(() => {
		const offered = new Set(groups.flatMap((group) => group.jobs.map((job) => job.id)));
		return selectedJobIds.filter((id) => !offered.has(id));
	}, [groups, selectedJobIds]);

	const items = useMemo<DropdownItem<string>[]>(() => {
		const out: DropdownItem<string>[] = [];
		for (const group of groups) {
			out.push({ value: `${GROUP_PREFIX}${group.key}`, label: group.label, groupValues: group.jobs.map((job) => job.id) });
			for (const job of group.jobs) out.push({ value: job.id, label: job.displayName, selectedLabel: job.displayName });
		}
		return out;
	}, [groups]);

	const handleJobCreated = useCallback(
		(job: Job) => {
			setDraftJobIds((prev) => (prev.includes(job.id) ? prev : [...prev, job.id]));
			onJobCreated(job);
		},
		[onJobCreated],
	);

	const handleConfirm = useCallback(() => {
		// Carry the un-offered ids back through: the picker never showed them, so it cannot be the
		// thing that removes them. Removing a job stays an explicit act in the grid.
		const confirmed = [...draftJobIds, ...unrepresentedJobIds.filter((id) => !draftJobIds.includes(id))];
		track("dept_manage_jobs_confirm", { source, job_count: confirmed.length });
		onConfirm(confirmed);
		onClose();
	}, [track, source, draftJobIds, unrepresentedJobIds, onConfirm, onClose]);

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("department.config.jobs.manage.dialog.hint")}</Typography>
			<SystemDropdown
				formControlSx={{ width: "100%" }}
				id={selectorId}
				label={t("enabled.jobs")}
				multiple
				fullWidth
				collapsibleGroups
				search
				items={items}
				selectedItems={draftJobIds}
				onChangeMultiple={setDraftJobIds}
				data-testid={`${dataTestId}-select`}
			/>
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }} data-testid={`${dataTestId}-count`}>
				{t("department.config.jobs.manage.dialog.selected", { count: draftJobIds.length })}
			</Typography>
			{departmentId && (
				<Box sx={{ display: "flex" }}>
					<SystemButton
						buttonVariant="borderless"
						size="small"
						leadingIcon={<AddCircleOutlined fontSize="small" />}
						onClick={() => setCreateJobOpen(true)}
						data-testid="dept-config-create-job-btn"
					>
						{t("department.config.create.job")}
					</SystemButton>
				</Box>
			)}
		</Box>
	);

	return (
		<>
			<SystemDialog
				open={open}
				onClose={onClose}
				maxWidth="sm"
				fullWidth
				title={t("department.config.jobs.manage.dialog.title")}
				body={body}
				cancel={t("cancel")}
				confirm={t("department.config.jobs.manage.dialog.confirm")}
				confirmButtonVariant="primary"
				onConfirm={handleConfirm}
				dataTestId={dataTestId}
			/>
			{departmentId && (
				<CreateJobDialog
					open={createJobOpen}
					onClose={() => setCreateJobOpen(false)}
					onSuccess={handleJobCreated}
					departmentId={departmentId}
					enabledJobIds={draftJobIds}
					existingJobNames={existingJobNames}
				/>
			)}
		</>
	);
};
